import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useSettings } from "@/contexts/SettingsContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Save,
  Loader2,
  Trash2,
  Archive,
  RotateCcw,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";
import RosterTab from "@/pages/RosterTab";
import CohortTracks from "@/pages/CohortTracks";
import CohortWelcomeTab from "@/pages/CohortWelcomeTab";
import CohortGradebook from "@/pages/CohortGradebook";
import EventsTab from "@/pages/EventsTab";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export default function CohortDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [cohort, setCohort] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [form, setForm] = useState({
    name: "",
    start_date: "",
    end_date: "",
    instructor: "",
    description: "",
  });

  const fetchCohort = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cohorts/${id}`, {
        withCredentials: true,
      });
      setCohort(data);
      setForm({
        name: data.name || "",
        start_date: data.start_date || "",
        end_date: data.end_date || "",
        instructor: data.instructor || "",
        description: data.description || "",
      });
    } catch {
      toast.error("Failed to load cohort");
      navigate("/admin/cohorts");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchCohort();
  }, [fetchCohort]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.start_date) payload.start_date = null;
      if (!payload.end_date) payload.end_date = null;
      if (!payload.instructor) payload.instructor = null;
      if (!payload.description) payload.description = null;
      const { data } = await axios.put(`${API}/cohorts/${id}`, payload, {
        withCredentials: true,
      });
      setCohort(data);
      toast.success("Cohort updated");
    } catch {
      toast.error("Failed to update cohort");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = cohort.status === "active" ? "archived" : "active";
    try {
      const { data } = await axios.put(
        `${API}/cohorts/${id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      setCohort(data);
      toast.success(`Cohort ${newStatus === "archived" ? "archived" : "reactivated"}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/cohorts/${id}`, { withCredentials: true });
      toast.success("Cohort deleted");
      navigate("/admin/cohorts");
    } catch {
      toast.error("Failed to delete cohort");
      throw new Error("delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2
          className="h-6 w-6 animate-spin"
          style={{ color: "var(--primary-color, #4A90D9)" }}
        />
      </div>
    );
  }

  if (!cohort) return null;

  const learnerLabel = settings.learner_label || "Learner";

  return (
    <div className="space-y-6 min-w-0" data-testid="cohort-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/cohorts")}
          data-testid="cohort-back-btn"
          className="text-[#A1A9B8] hover:text-white hover:bg-white/5 w-fit"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to Cohorts
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <h2
            className="font-heading text-xl sm:text-2xl font-bold text-white tracking-tight truncate"
            data-testid="cohort-detail-name"
          >
            {cohort.name}
          </h2>
          <Badge
            data-testid="cohort-detail-status"
            className={
              cohort.status === "active"
                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                : "bg-[#6E7787]/15 text-[#6E7787] border-[#6E7787]/20"
            }
          >
            {cohort.status}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleStatus}
            data-testid="cohort-toggle-status"
            className="text-[#A1A9B8] hover:text-white hover:bg-white/5"
          >
            {cohort.status === "active" ? (
              <>
                <Archive className="h-4 w-4 mr-1.5" />
                Archive
              </>
            ) : (
              <>
                <RotateCcw className="h-4 w-4 mr-1.5" />
                Reactivate
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            disabled={deleting}
            data-testid="cohort-delete-btn"
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete
          </Button>
        </div>
      </div>

      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        tier={3}
        entityName={cohort?.name}
        entityType="cohort"
        entityId={id}
        onConfirm={handleDelete}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        {/* Mobile: dropdown selector */}
        <div className="md:hidden">
          <label className="sr-only" htmlFor="cohort-tab-select">Section</label>
          <select
            id="cohort-tab-select"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            data-testid="cohort-tab-select-mobile"
            className="w-full h-10 rounded-md bg-[#11141A] border border-white/10 text-white text-sm font-body px-3 focus:outline-none focus:border-white/20"
          >
            <option value="overview">Overview</option>
            <option value="welcome">Welcome</option>
            <option value="roster">Roster</option>
            <option value="tracks">Tracks</option>
            <option value="gradebook">Gradebook</option>
            <option value="events">Events</option>
          </select>
        </div>

        {/* Desktop / tablet: tab strip */}
        <TabsList className="hidden md:flex bg-[#11141A] border border-white/10 p-1 w-full flex-nowrap overflow-x-auto scrollbar-none gap-0">
          <TabsTrigger
            value="overview"
            data-testid="tab-overview"
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-[#6E7787] font-body flex-shrink-0 text-xs sm:text-sm"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="welcome"
            data-testid="tab-welcome"
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-[#6E7787] font-body flex-shrink-0 text-xs sm:text-sm"
          >
            Welcome
          </TabsTrigger>
          <TabsTrigger
            value="roster"
            data-testid="tab-roster"
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-[#6E7787] font-body flex-shrink-0 text-xs sm:text-sm"
          >
            Roster
          </TabsTrigger>
          <TabsTrigger
            value="tracks"
            data-testid="tab-tracks"
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-[#6E7787] font-body flex-shrink-0 text-xs sm:text-sm"
          >
            Tracks
          </TabsTrigger>
          <TabsTrigger
            value="gradebook"
            data-testid="tab-gradebook"
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-[#6E7787] font-body flex-shrink-0 text-xs sm:text-sm"
          >
            Gradebook
          </TabsTrigger>
          <TabsTrigger
            value="events"
            data-testid="tab-events"
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-[#6E7787] font-body flex-shrink-0 text-xs sm:text-sm"
          >
            Events
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <form onSubmit={handleSave} className="space-y-6">
            <div className="bg-[#11141A] border border-white/10 rounded-xl p-6 space-y-5">
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">
                  Cohort Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="edit-cohort-name"
                  className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[#A1A9B8] font-body text-sm">Start Date</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    data-testid="edit-cohort-start-date"
                    className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10 [color-scheme:dark]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#A1A9B8] font-body text-sm">End Date</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    data-testid="edit-cohort-end-date"
                    className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10 [color-scheme:dark]"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">Instructor</Label>
                <Input
                  value={form.instructor}
                  onChange={(e) => setForm({ ...form, instructor: e.target.value })}
                  placeholder="Instructor name"
                  data-testid="edit-cohort-instructor"
                  className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] h-10"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description"
                  rows={4}
                  data-testid="edit-cohort-description"
                  className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] resize-none"
                />
              </div>
            </div>

            {/* Meta info */}
            <div className="flex flex-wrap gap-6 text-xs text-[#6E7787] font-body">
              <span>Created: {formatDate(cohort.created_at)}</span>
              <span>Updated: {formatDate(cohort.updated_at)}</span>
            </div>

            <Button
              type="submit"
              disabled={saving || !form.name.trim()}
              data-testid="save-cohort-btn"
              className="h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1.5" />
                  Save Changes
                </>
              )}
            </Button>
          </form>
        </TabsContent>

        {/* Roster Tab */}
        <TabsContent value="roster">
          <RosterTab cohortId={id} cohortStatus={cohort.status} />
        </TabsContent>

        {/* Tracks Tab */}
        <TabsContent value="tracks">
          <CohortTracks cohortId={id} cohortStatus={cohort.status} />
        </TabsContent>

        {/* Assessments Tab */}
        <TabsContent value="welcome">
          <div className="bg-[#11141A] border border-white/10 rounded-xl p-6">
            <CohortWelcomeTab cohortId={id} />
          </div>
        </TabsContent>

        {/* Gradebook Tab */}
        <TabsContent value="gradebook">
          <div className="bg-[#11141A] border border-white/10 rounded-xl p-6">
            <CohortGradebook cohortId={id} />
          </div>
        </TabsContent>

        <TabsContent value="events">
          <EventsTab cohortId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
